// BPM-5360: set _price_override and _plant on add-to-cart (not via post-cart /cart/change.js) to limit API calls.
/**
 * Baseline price override lookup (DOM only). rs-realtime-pricing.js replaces this
 * with a version that also uses the in-memory RTP cache.
 * @param {ParentNode | Element | null | undefined} scope
 * @param {{ productId?: string | number, variantId?: string | number, sku?: string }} [options]
 */
function getPriceOverrideLinePropertiesBasic(scope, options = {}) {
  /** @type {{ _price_override?: string, _price_override_reason?: string }} */
  const empty = {};
  let productId = options.productId != null ? String(options.productId) : undefined;
  let variantId = options.variantId != null ? String(options.variantId) : undefined;
  let sku = options.sku ? String(options.sku).trim() : undefined;

  if (scope instanceof Element) {
    if (!productId) {
      productId = scope.closest('product-form-component')?.dataset?.productId
        || scope.closest('[data-product-id]')?.dataset?.productId;
    }
    if (!variantId) {
      const form = scope.closest('form');
      const idInput = form?.querySelector('input[name="id"]');
      if (idInput instanceof HTMLInputElement && idInput.value) variantId = idInput.value;
      const atcBtn = scope.querySelector('[data-variant-id]')
        || scope.closest('add-to-cart-element, add-to-cart')?.querySelector('[data-variant-id]');
      if (atcBtn instanceof HTMLElement && atcBtn.dataset.variantId) {
        variantId = atcBtn.dataset.variantId;
      }
    }
    if (!sku) {
      const rtpHost = scope.matches('real-time-price-element, price-real-time')
        ? scope
        : scope.querySelector('real-time-price-element, price-real-time');
      if (rtpHost instanceof HTMLElement) {
        sku = rtpHost.getAttribute('data-pricing-sku')
          || rtpHost.querySelector('[data-sku]')?.dataset?.sku
          || rtpHost.dataset.sku;
      }
    }

    const selectors = [
      'real-time-price-element',
      'price-real-time .price',
      'price-real-time',
      '.price',
    ];
    for (const selector of selectors) {
      const match = scope.matches(selector) ? scope : scope.querySelector(selector);
      if (match instanceof HTMLElement && match.dataset.realtimePrice) {
        return {
          _price_override: String(match.dataset.realtimePrice),
          _price_override_reason: 'realtime_price',
        };
      }
    }
    if (scope instanceof HTMLElement && scope.dataset.realtimePrice) {
      return {
        _price_override: String(scope.dataset.realtimePrice),
        _price_override_reason: 'realtime_price',
      };
    }
  }

  if (variantId) {
    const RTP = window.RTP;
    const cachedVariant = RTP?.pricesByVariantId?.get(String(variantId));
    if (cachedVariant) {
      return {
        _price_override: String(cachedVariant),
        _price_override_reason: 'realtime_price',
      };
    }
  }

  if (sku) {
    const RTP = window.RTP;
    const cachedSku = RTP?.pricesBySku?.get(String(sku));
    if (cachedSku) {
      return {
        _price_override: String(cachedSku),
        _price_override_reason: 'realtime_price',
      };
    }
    const skuHost = document.querySelector(`[data-sku="${CSS.escape(String(sku))}"]`);
    const skuPrice = skuHost?.querySelector('.price') || skuHost;
    if (skuPrice instanceof HTMLElement && skuPrice.dataset.realtimePrice) {
      return {
        _price_override: String(skuPrice.dataset.realtimePrice),
        _price_override_reason: 'realtime_price',
      };
    }
  }

  if (variantId) {
    const byVariant = document.querySelector(`[data-product-variant-id="${variantId}"] .price`);
    if (byVariant instanceof HTMLElement && byVariant.dataset.realtimePrice) {
      return {
        _price_override: String(byVariant.dataset.realtimePrice),
        _price_override_reason: 'realtime_price',
      };
    }
  }

  if (productId) {
    const byProduct = document.querySelector(`[data-product-id="${productId}"] .price`);
    if (byProduct instanceof HTMLElement && byProduct.dataset.realtimePrice) {
      return {
        _price_override: String(byProduct.dataset.realtimePrice),
        _price_override_reason: 'realtime_price',
      };
    }
  }

  return empty;
}

if (typeof window.getPriceOverrideLineProperties !== 'function') {
  window.getPriceOverrideLineProperties = getPriceOverrideLinePropertiesBasic;
}

/**
 * Merge realtime price override and plant line item properties for add-to-cart.
 * BPM-5360: central helper so every add path sets properties once instead of patching the cart later.
 * @param {ParentNode | Element | null | undefined} scope
 * @param {Record<string, unknown>} [existingProperties]
 * @param {{ productId?: string | number, variantId?: string | number, sku?: string }} [options]
 * @returns {Record<string, string>}
 */
function mergeAddToCartLineProperties(scope, existingProperties = {}, options = {}) {
  /** @type {Record<string, string>} */
  let merged = {};
  Object.entries(existingProperties).forEach(([key, value]) => {
    if (value != null && value !== '') merged[key] = String(value);
  });

  if (typeof window.getPriceOverrideLineProperties === 'function') {
    Object.entries(window.getPriceOverrideLineProperties(scope, options)).forEach(([key, value]) => {
      if (value != null && value !== '') merged[key] = String(value);
    });
  }

  if (typeof window.getPlantLineProperty === 'function') {
    Object.entries(window.getPlantLineProperty(scope, options)).forEach(([key, value]) => {
      if (value != null && value !== '') merged[key] = String(value);
    });
    // Retry plant lookup after scanning the DOM when sales-data hosts render after the first merge call.
    if (!merged._plant && typeof window.bootstrapPlantCacheFromDocument === 'function') {
      window.bootstrapPlantCacheFromDocument();
      Object.entries(window.getPlantLineProperty(scope, options)).forEach(([key, value]) => {
        if (value != null && value !== '') merged[key] = String(value);
      });
    }
  } else if (typeof window.bootstrapPlantCacheFromDocument === 'function') {
    window.bootstrapPlantCacheFromDocument();
    const variantId = options.variantId != null ? String(options.variantId) : undefined;
    const cached = variantId ? window.RTP?.plantsByVariantId?.get(variantId) : null;
    if (cached) merged._plant = String(cached);
  }

  // Always attach _sku when provided. Plant lookup only sometimes returned _sku, so
  // callers that pass options.sku (lists, PLP, quick order) were intermittent.
  let skuFromOptions = options.sku != null ? String(options.sku).trim() : '';
  if (!skuFromOptions && !merged._sku && scope instanceof Element) {
    const skuInput =
      (scope.matches?.('input[name="sku"], input[name="material-number"]') && scope) ||
      scope.querySelector?.('input[name="sku"], input[name="material-number"]') ||
      scope.closest?.('form')?.querySelector('input[name="sku"], input[name="material-number"]');
    if (skuInput instanceof HTMLInputElement && skuInput.value?.trim()) {
      skuFromOptions = skuInput.value.trim();
    } else {
      const dataSkuHost =
        scope.matches?.('[data-sku]') ? scope :
        scope.querySelector?.('[data-sku]') ||
        scope.closest?.('[data-sku]');
      if (dataSkuHost instanceof HTMLElement && dataSkuHost.dataset.sku?.trim()) {
        skuFromOptions = dataSkuHost.dataset.sku.trim();
      }
    }
  }
  if (skuFromOptions) {
    merged._sku = skuFromOptions;
  }

  return merged;
}

window.mergeAddToCartLineProperties = mergeAddToCartLineProperties;

/** Soft cart ceiling: distinct Shopify line items (SKUs), not unit quantity. */
const RS_MAX_CART_LINE_ITEMS = 120;

// Below Shopify's DraftOrderInput.lineItems hard cap (499) enforced server-side in
// customer-resideo-shopify-app's api/routes/api/checkout/POST-checkout-with-billing.ts
// (MAX_DRAFT_ORDER_LINE_ITEMS).
/** Draft-order checkout carries pricing explicitly and tolerates more lines than standard cart. */
const RS_MAX_CART_LINE_ITEMS_DRAFT_ORDER = 200;

/**
 * Resolve the active line-item ceiling. Draft-order checkout (B2B_DRAFT_ORDER_CHECKOUT_ENABLED)
 * uses a higher ceiling since it bypasses the standard cart/checkout pricing transform.
 * @returns {number}
 */
function getMaxCartLineItems() {
  return window.B2B_DRAFT_ORDER_CHECKOUT_ENABLED
    ? RS_MAX_CART_LINE_ITEMS_DRAFT_ORDER
    : RS_MAX_CART_LINE_ITEMS;
}

/**
 * @param {number} [max]
 * @returns {string}
 */
function cartLineLimitMessage(max = getMaxCartLineItems()) {
  if (typeof window.__t === 'function') {
    const msg = window.__t('cart_line_item_limit', { count: max });
    if (msg && msg !== 'cart_line_item_limit') return msg;
  }
  return `Your cart is limited to ${max} SKUs. Please remove excess items before adding more.`;
}

/**
 * Parse variant ids from a `/cart/add` request body.
 * @param {RequestInit | undefined} init
 * @returns {Array<{ id: string }>}
 */
function extractCartAddItemsFromInit(init) {
  if (!init?.body) return [];

  /** @param {unknown} value @returns {string | null} */
  const normalizeId = (value) => {
    if (value == null || value === '') return null;
    const raw = String(value).trim();
    if (!raw) return null;
    if (raw.startsWith('gid://shopify/ProductVariant/')) {
      return raw.slice('gid://shopify/ProductVariant/'.length);
    }
    return raw;
  };

  if (init.body instanceof FormData) {
    const id = normalizeId(init.body.get('id'));
    return id ? [{ id }] : [];
  }

  if (typeof init.body === 'string') {
    const trimmed = init.body.trim();
    if (trimmed.startsWith('{')) {
      try {
        const payload = JSON.parse(trimmed);
        if (Array.isArray(payload?.items)) {
          return payload.items
            .map(/** @param {any} item */ (item) => normalizeId(item?.id ?? item?.variant_id))
            .filter(Boolean)
            .map(/** @param {string} id */ (id) => ({ id }));
        }
        const id = normalizeId(payload?.id ?? payload?.variant_id);
        return id ? [{ id }] : [];
      } catch {
        return [];
      }
    }

    try {
      const params = new URLSearchParams(init.body);
      const id = normalizeId(params.get('id'));
      return id ? [{ id }] : [];
    } catch {
      return [];
    }
  }

  return [];
}

/**
 * Count how many of the requested adds would create new cart lines.
 * Treats same variant_id as a merge (matches PDP / quick-add merge behavior).
 * @param {Array<{ variant_id?: string | number, id?: string | number }> | undefined} cartItems
 * @param {Array<{ id: string }>} itemsToAdd
 * @returns {number}
 */
function countNewCartLines(cartItems, itemsToAdd) {
  const existing = new Set(
    (cartItems || [])
      .map((item) => String(item.variant_id ?? item.id ?? '').trim())
      .filter(Boolean),
  );
  const seenInRequest = new Set();
  let newCount = 0;
  for (const item of itemsToAdd) {
    const id = String(item?.id ?? '').trim();
    if (!id || existing.has(id) || seenInRequest.has(id)) continue;
    seenInRequest.add(id);
    newCount += 1;
  }
  return newCount;
}

/**
 * @param {Array<{ id: string }>} itemsToAdd
 * @param {{ max?: number }} [options]
 * @returns {Promise<{ ok: boolean, current: number, newLines: number, max: number, remaining: number, message?: string }>}
 */
async function checkCartLineCapacity(itemsToAdd, options = {}) {
  const max = options.max ?? getMaxCartLineItems();
  const empty = {
    ok: true,
    current: 0,
    newLines: 0,
    max,
    remaining: max,
  };
  if (!Array.isArray(itemsToAdd) || itemsToAdd.length === 0) return empty;

  try {
    const cartRes = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!cartRes.ok) return empty;

    const cart = await cartRes.json();
    const cartItems = Array.isArray(cart?.items) ? cart.items : [];
    const current = cartItems.length;
    const newLines = countNewCartLines(cartItems, itemsToAdd);
    const remaining = Math.max(0, max - current);

    // Hard ceiling: never allow /cart/add at/above the limit.
    // PLP often POSTs /cart/add for variants already in cart; different line
    // properties (_plant, _price_override) make Shopify create a *new* line,
    // so treating "variant exists" as a safe merge under-counts and lets carts
    // grow past the limit. Qty increases must use /cart/change or /cart/update.
    if (current >= max) {
      return {
        ok: false,
        current,
        newLines,
        max,
        remaining: 0,
        message: cartLineLimitMessage(max),
      };
    }

    if (current + newLines > max) {
      return {
        ok: false,
        current,
        newLines,
        max,
        remaining,
        message: cartLineLimitMessage(max),
      };
    }

    return { ok: true, current, newLines, max, remaining };
  } catch (error) {
    console.warn('[cart-line-limit] capacity check failed; allowing add', error);
    return empty;
  }
}

/** @type {Promise<void>} */
let addOrIncrementCartLineGate = Promise.resolve();

/**
 * Add a new line or increment an existing one (PDP/PLP-safe).
 * Uses /cart/change when the variant is already in cart so we never create
 * duplicate lines via property mismatch, and respects the line-item ceiling.
 * Serialized so concurrent collection-page clicks cannot both pass a stale
 * cart snapshot.
 * @param {{ id: string | number, quantity?: number, properties?: Record<string, unknown> }} line
 * @returns {Promise<{ ok: true, mode: 'add' | 'change', data: any } | { ok: false, message: string, data?: any }>}
 */
async function addOrIncrementCartLine(line) {
  const run = async () => {
    const variantId = Number(line?.id);
    const quantity = Math.max(1, Number(line?.quantity) || 1);
    const properties =
      line?.properties && typeof line.properties === 'object' ? line.properties : {};

    if (!Number.isFinite(variantId) || variantId <= 0) {
      return { ok: false, message: cartLineLimitMessage() };
    }

    const cartRes = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!cartRes.ok) {
      return { ok: false, message: cartLineLimitMessage() };
    }
    const cartState = await cartRes.json();
    const cartItems = Array.isArray(cartState?.items) ? cartState.items : [];
    const existing = cartItems.find(
      /** @param {any} item */ (item) => Number(item.variant_id) === variantId,
    );
    const maxLines = getMaxCartLineItems();

    if (existing) {
      const changeRes = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          id: existing.key,
          quantity: existing.quantity + quantity,
          properties: {
            ...(existing.properties || {}),
            ...Object.fromEntries(
              Object.entries(properties).filter(([, value]) => value != null && value !== ''),
            ),
          },
        }),
      });
      const changeJson = await changeRes.json().catch(() => ({}));
      if (!changeRes.ok) {
        return {
          ok: false,
          message:
            changeJson?.description ||
            changeJson?.message ||
            `Failed to update cart (HTTP ${changeRes.status})`,
          data: changeJson,
        };
      }
      return { ok: true, mode: 'change', data: changeJson };
    }

    if (cartItems.length >= maxLines) {
      return { ok: false, message: cartLineLimitMessage(maxLines) };
    }

    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity,
            ...(Object.keys(properties).length ? { properties } : {}),
          },
        ],
      }),
    });
    const addJson = await addRes.json().catch(() => ({}));
    if (!addRes.ok) {
      return {
        ok: false,
        message:
          addJson?.description ||
          addJson?.message ||
          `Failed to add to cart (HTTP ${addRes.status})`,
        data: addJson,
      };
    }
    return { ok: true, mode: 'add', data: addJson };
  };

  const result = addOrIncrementCartLineGate.then(run, run);
  addOrIncrementCartLineGate = result.then(
    () => undefined,
    () => undefined,
  );
  return result;
}

/**
 * @param {string} message
 * @returns {Response}
 */
function cartLineLimitResponse(message) {
  return new Response(
    JSON.stringify({
      status: 422,
      message,
      description: message,
    }),
    {
      status: 422,
      headers: {
        'Content-Type': 'application/json',
        'X-Rs-Cart-Line-Limit': '1',
      },
    },
  );
}

/** @type {Promise<void>} */
let cartAddCapacityGate = Promise.resolve();

/**
 * Serialize /cart/add capacity checks + requests so concurrent PLP clicks
 * cannot both pass a stale cart.js snapshot and exceed the line ceiling.
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function withCartAddCapacityGate(task) {
  const run = cartAddCapacityGate.then(task, task);
  cartAddCapacityGate = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

window.RS_MAX_CART_LINE_ITEMS = getMaxCartLineItems();
window.getMaxCartLineItems = getMaxCartLineItems;
window.checkCartLineCapacity = checkCartLineCapacity;
window.cartLineLimitMessage = cartLineLimitMessage;
window.addOrIncrementCartLine = addOrIncrementCartLine;

/**
 * Patch cart add requests with realtime price override line item properties.
 * Installed early so it runs before rs-realtime-pricing.js finishes loading.
 * @param {RequestInit} init
 * @returns {Promise<RequestInit>}
 */
async function augmentCartAddRequestInit(init) {
  if (!init?.body) return init;

  const headers = init.headers instanceof Headers
    ? Object.fromEntries(init.headers.entries())
    : /** @type {Record<string, string>} */ (init.headers || {});
  const contentType = String(headers['Content-Type'] || headers['content-type'] || '');

  /** @param {string | number | null | undefined} variantId @param {string | undefined} sku @param {Record<string, unknown>} [existingProperties] */
  const propertiesForItem = (variantId, sku, existingProperties = {}) => mergeAddToCartLineProperties(null, existingProperties, {
    variantId: variantId != null ? String(variantId) : undefined,
    sku,
  });

  if (init.body instanceof FormData) {
    const fd = init.body;
    /** @type {Record<string, string>} */
    const existing = {};
    fd.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) existing[match[1]] = String(value);
    });
    const merged = propertiesForItem(fd.get('id'), undefined, existing);
    const hasChanges = Object.entries(merged).some(([key, value]) => existing[key] !== value);
    if (!hasChanges) return init;
    const nextFd = new FormData();
    fd.forEach((value, key) => {
      if (!/^properties\[/.test(key)) nextFd.append(key, value);
    });
    Object.entries(merged).forEach(([key, value]) => {
      nextFd.append(`properties[${key}]`, String(value));
    });
    return {...init, body: nextFd};
  }

  if (typeof init.body === 'string') {
    if (contentType.includes('application/json') || init.body.trim().startsWith('{')) {
      let payload;
      try {
        payload = JSON.parse(init.body);
      } catch {
        return init;
      }
      const patched = patchCartAddPayload(payload, propertiesForItem);
      if (!patched) return init;
      return {
        ...init,
        body: JSON.stringify(patched),
        headers: {...headers, 'Content-Type': 'application/json'},
      };
    }

    const params = new URLSearchParams(init.body);
    /** @type {Record<string, string>} */
    const existing = {};
    params.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match) existing[match[1]] = value;
    });
    const merged = propertiesForItem(params.get('id'), undefined, existing);
    const hasChanges = Object.entries(merged).some(([key, value]) => existing[key] !== value);
    if (!hasChanges) return init;
    [...params.keys()].forEach((key) => {
      if (/^properties\[/.test(key)) params.delete(key);
    });
    Object.entries(merged).forEach(([key, value]) => {
      params.set(`properties[${key}]`, String(value));
    });
    return {...init, body: params.toString()};
  }

  return init;
}

/**
 * @param {any} payload
 * @param {(variantId: string | number | null | undefined, sku?: string, existingProperties?: Record<string, unknown>) => Record<string, string>} propertiesForItem
 * @returns {any | null}
 */
function patchCartAddPayload(payload, propertiesForItem) {
  if (!payload || typeof payload !== 'object') return null;

  /** @param {any} item */
  const patchItem = (item) => {
    if (!item || item.id == null) return item;
    const existing = item.properties && typeof item.properties === 'object' ? item.properties : {};
    const sku =
      (item.sku != null && String(item.sku).trim()) ||
      (existing._sku != null && String(existing._sku).trim()) ||
      undefined;
    const merged = propertiesForItem(item.id, sku, existing);
    const changed = Object.entries(merged).some(([key, value]) => existing[key] !== value);
    return changed ? {...item, properties: merged} : item;
  };

  if (Array.isArray(payload.items)) {
    const items = payload.items.map(patchItem);
    const changed = items.some((item, index) => item !== payload.items[index]);
    return changed ? {...payload, items} : null;
  }

  if (payload.id != null) {
    const patched = patchItem({...payload, quantity: payload.quantity ?? 1});
    return patched !== payload ? patched : null;
  }

  return null;
}


// Intercept every /cart/add so _price_override and _plant are set even when callers omit line properties.
// Also enforce the line-item ceiling before the request reaches Shopify.
if (!window.__rsCartAddPriceOverrideHookInstalled) {
  window.__rsCartAddPriceOverrideHookInstalled = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string'
      ? input
      : input instanceof Request
        ? input.url
        : '';
    const isCartAdd = /\/cart\/add(\.js)?(\?|$)/.test(url);

    if (!isCartAdd) {
      return nativeFetch(input, init);
    }

    const nextInit = await augmentCartAddRequestInit(init);

    return withCartAddCapacityGate(async () => {
      const itemsToAdd = extractCartAddItemsFromInit(nextInit);
      const capacity = await checkCartLineCapacity(itemsToAdd);
      if (!capacity.ok) {
        return cartLineLimitResponse(capacity.message || cartLineLimitMessage());
      }
      return nativeFetch(input, nextInit);
    });
  };
}

const RS_DISMISSED_BANNERS_KEY = 'rs-dismissed-banners';

function readDismissedBannerIds() {
  try {
    const stored = localStorage.getItem(RS_DISMISSED_BANNERS_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function rememberDismissedBanner(id) {
  if (!id) return;
  const bannerId = String(id);
  const dismissed = readDismissedBannerIds();
  if (dismissed.includes(bannerId)) return;
  dismissed.push(bannerId);
  try {
    localStorage.setItem(RS_DISMISSED_BANNERS_KEY, JSON.stringify(dismissed));
  } catch (_) {}
}

function hideDismissedBanners() {
  readDismissedBannerIds().forEach(function (id) {
    document.getElementById('rs-banner-' + id)?.remove();
  });
}

document.addEventListener('DOMContentLoaded', function () {

  hideDismissedBanners();

  // Close banner buttons (delegated so dynamically shown banners work too)
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('.rs-banner__close-button');
    if (!btn) return;

    const banner = btn.closest('.rs-banner');
    if (!banner) return;

    // Toast / message banners must stay in the DOM so they can be shown again.
    if (
      banner.classList.contains('rs-banner--visible') ||
      banner.closest('.rs-product-list-detail__notifications') ||
      banner.closest('.cart-page-actions__items-removed-banner') ||
      banner.dataset.persist === '1'
    ) {
      banner.classList.remove('rs-banner--visible');
      banner.style.removeProperty('opacity');
      banner.style.removeProperty('transform');
      banner.style.removeProperty('transition');
      if (banner.closest('.rs-product-list-detail__notifications')) {
        document.dispatchEvent(
          new CustomEvent('rs-pld:notification-banner-closed', { detail: { banner } })
        );
      }
      return;
    }

    const id = btn.getAttribute('data-banner-id');
    if (id) rememberDismissedBanner(id);

    banner.style.transition = 'opacity .18s ease, transform .18s ease';
    banner.style.opacity = '0';
    banner.style.transform = 'translateY(-6px)';
    setTimeout(function () { banner.remove(); }, 200);
  });

    function isValidQuantity(val, minOrder, orderMultiple) {
        const valN = Number(val);
        const mo = Math.floor(Number(minOrder));
        const st = Math.floor(Number(orderMultiple));
        if (!Number.isFinite(valN) || !Number.isFinite(mo) || valN < mo) return false;
        if (!Number.isFinite(st) || st <= 1) return true;
        return valN === mo || (valN % st === 0 && valN >= mo);
    }

    function resolveMinFromInput(input) {
        const min = parseInt(input.getAttribute('min'), 10);
        if (Number.isFinite(min) && min >= 1) return min;
        const dataMin = parseInt(input.getAttribute('data-min'), 10);
        if (Number.isFinite(dataMin) && dataMin >= 1) return dataMin;
        return 1;
    }

    function resolveStepFromInput(input) {
        const stepAttr = input.getAttribute('step');
        const skipIncrement = stepAttr === 'any' || stepAttr === '' || stepAttr == null;
        if (skipIncrement) return 1;
        const step = parseFloat(stepAttr);
        return Number.isFinite(step) && step > 0 ? step : 1;
    }

    function getQuantityValidationContext(input) {
        const quantityWrapper = input.closest('.rs-quantity-input-wrapper');
        const minOrder = resolveMinFromInput(input);
        const orderMultiple = resolveStepFromInput(input);
        const stepAttr = input.getAttribute('step');
        const skipIncrement = stepAttr === 'any' || stepAttr === '' || stepAttr == null;
        return { quantityWrapper, minOrder, orderMultiple, skipIncrement };
    }

    function clearQuantityError(wrapper) {
        if (!wrapper) return;
        wrapper.classList.remove('rs-error');
        wrapper.closest('product-form-component')?.classList.remove('rs-error');
        const errorBox = wrapper.querySelector('.rs-error__message');
        const errorText = wrapper.querySelector('.rs-error__message-text') || wrapper.querySelector('.rs-input__error span');
        if (errorText) errorText.textContent = '';
        if (errorBox instanceof HTMLElement) {
            errorBox.style.display = '';
            errorBox.hidden = false;
        }
        const qtyWrapper = wrapper.classList?.contains('rs-quantity-input-wrapper')
            ? wrapper
            : wrapper.querySelector?.('.rs-quantity-input-wrapper');
        if (qtyWrapper?.closest('.rs-buy-buttons-block')) {
            window.rsQtyErrorSpacing?.clear(qtyWrapper);
        }
    }

    function showQuantityError(wrapper, message) {
        if (!wrapper) return;
        const qtyWrapper = wrapper.classList?.contains('rs-quantity-input-wrapper')
            ? wrapper
            : wrapper.querySelector?.('.rs-quantity-input-wrapper') || wrapper;
        const errorBox = qtyWrapper.querySelector('.rs-error__message');
        const errorText = qtyWrapper.querySelector('.rs-error__message-text') || qtyWrapper.querySelector('.rs-input__error span');
        if (errorText && message) errorText.textContent = message;
        qtyWrapper.classList.add('rs-error');
        qtyWrapper.closest('product-form-component')?.classList.add('rs-error');
        if (errorBox instanceof HTMLElement) errorBox.style.display = '';
        if (qtyWrapper.closest('.rs-buy-buttons-block') && errorBox instanceof HTMLElement) {
            window.rsQtyErrorSpacing?.update(qtyWrapper, errorBox);
        }
    }

    /* ===================================================================
    #   Quantity Input Validation
    # ================================================================= */
    const quantityInputs = document.querySelectorAll('.rs-quantity-input');
    quantityInputs?.forEach(input => {
        var maxLenAttr = input.getAttribute('maxlength');
        var maxDigits =
            maxLenAttr != null && maxLenAttr !== ''
                ? parseInt(maxLenAttr, 10)
                : NaN;
        if (Number.isFinite(maxDigits) && maxDigits > 0) {
            input.addEventListener('input', function rsQtyMaxDigitsClamp() {
                var cleaned = input.value.replace(/\D/g, '').slice(0, maxDigits);
                if (input.value !== cleaned) {
                    input.value = cleaned;
                    try {
                        input.setSelectionRange(cleaned.length, cleaned.length);
                    } catch (_) {}
                }
            });
        }

        input.addEventListener('input', function () {
            if (input.closest('.delivery-dates-modal')) return;
            const { quantityWrapper, minOrder, orderMultiple } = getQuantityValidationContext(input);
            if (!quantityWrapper) return;
            const val = parseInt(input.value, 10);
            if (!Number.isFinite(val) || !Number.isFinite(minOrder) || minOrder < 1) return;
            if (isValidQuantity(val, minOrder, orderMultiple)) {
                clearQuantityError(quantityWrapper);
            }
        });

        input.addEventListener('change', function () {
            if (input.closest('.delivery-dates-modal')) return;
            if (input.closest('.rs-buy-buttons-block') && typeof window.rsValidateProductCardQtyInput === 'function') {
                window.rsValidateProductCardQtyInput(input);
                return;
            }
            const { quantityWrapper, minOrder, orderMultiple, skipIncrement } = getQuantityValidationContext(input);
            const quickOrderRow = input.closest('.quick-order-table .rs-table-body__row');
            if (quickOrderRow) {
                const sku = String(quickOrderRow.querySelector('.rs-material-number-input-wrapper input')?.value ?? '').trim();
                if (!sku) {
                    clearQuantityError(quantityWrapper);
                    return;
                }
            }
            let wrapper = quantityWrapper || input.closest('product-form-component') || input.closest('.rs-table-body__cell');
            const resolvedMin = resolveMinFromInput(input);
            const resolvedStep = resolveStepFromInput(input);

            let val = input.value === "0" ? resolvedMin : parseInt(input.value, 10);
            if (!Number.isFinite(val)) {
                val = resolvedMin;
            }
            if (val < resolvedMin) {
                showQuantityError(wrapper, /** @type {any} */ (window).__t('minimum_order', { count: resolvedMin }));
                return;
            }

            if (!skipIncrement && !isValidQuantity(val, resolvedMin, resolvedStep)) {
                showQuantityError(wrapper, /** @type {any} */ (window).__t('order_in_multiples', { count: resolvedStep }));
                return;
            }
            clearQuantityError(wrapper);
        });
    });

    /* ===================================================================
    #   Add To Cart Button Flow
    # =================================================================== */
    
    function shopifyRoot() {
        return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    }
    
    function collectCartSectionIds() {
        if (window.rsCart?.sectionIds?.length) {
            return [...new Set(window.rsCart.sectionIds)].filter(id => !/header/i.test(String(id)) && !/modal/i.test(String(id)));
        }
        const out = new Set();
        document.querySelectorAll('[id^="shopify-section-"]').forEach(el => {
            const sid = el.id.replace('shopify-section-', '');
            // Skip modals: IDs like rs_quick_add_to_cart_modal_* match /cart/i and
            // replacing them while open tears down the confirmation/success dialog.
            if (/modal/i.test(sid) || /header/i.test(sid)) return;
            if (/cart/i.test(sid) || el.hasAttribute('data-refresh-on-cart')) out.add(sid);
        });
        return [...out];
    }

    function updateHeaderCartDisplay(cart) {
        const itemCount = cart?.item_count ?? 0;
        const lineCount = Array.isArray(cart?.items) ? cart.items.length : 0;

        document.querySelectorAll('[data-cart-count], .cart-count-bubble').forEach(el => {
            if ('textContent' in el) el.textContent = String(itemCount);
            el.classList.toggle('hidden', itemCount === 0);
        });

        const cartLink =
            document.querySelector('.header-actions__action.action__cart') ||
            document.querySelector('.header-actions__action.rs-cart-drawer__button');

        if (!cartLink) return;

        cartLink.setAttribute('aria-label', `Cart Total items in cart: ${itemCount}`);

        const details = cartLink.querySelector('.rs-cart-drawer__button-details');
        if (details) {
            const countEl = details.querySelector('.text-small') || details.querySelector('span');
            const totalEl =
                details.querySelector('[data-cart-total-price]') ||
                details.querySelector('.text-xsmall') ||
                details.querySelectorAll('span')[1];

            if (countEl) {
                countEl.textContent = lineCount === 1 ? '1 SKU' : `${lineCount} SKUs`;
            }

            if (totalEl) {
                const realtimeOwnsTotal = totalEl.hasAttribute('data-cart-total-price');
                if (itemCount === 0) {
                    totalEl.textContent = '';
                    totalEl.classList.add('rs-hidden');
                } else if (!realtimeOwnsTotal && cart.total_price > 0) {
                    totalEl.textContent = new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: cart.currency || 'USD',
                    }).format(cart.total_price / 100);
                    totalEl.classList.remove('rs-hidden');
                }
            }
        }

        const icon = cartLink.querySelector('cart-icon');
        if (icon) {
            icon.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);
        }

        const dotParentEl = cartLink.querySelector('.rs-cart-drawer__button-icon-wrapper');
        if (dotParentEl && !dotParentEl.querySelector('.rs-cart-drawer__button-icon-notempty')) {
          const dotEl = document.createElement('span');
          dotEl.className = 'rs-cart-drawer__button-icon-notempty';
          dotParentEl.appendChild(dotEl);
        }

        const dot = cartLink.querySelector('.rs-cart-drawer__button-icon-notempty');
        if (dot) {
            dot.style.display = itemCount > 0 ? '' : 'none';
        }
    }

    window.rsUpdateHeaderCartDisplay = updateHeaderCartDisplay;
    
    async function updateCartCountFallback() {
        try {
            const cartRes = await fetch('/cart.js', {
                headers: { 'Accept': 'application/json' },
                credentials: 'same-origin'
            });
            const cart = await cartRes.json();
            updateHeaderCartDisplay(cart);
        } catch (e) {
            console.error('Fallback count update failed:', e);
        }
    }
    
    async function refreshSections() {
        const ids = collectCartSectionIds();
        if (!ids.length) {
            await updateCartCountFallback();
            return;
        }
        const url = `${shopifyRoot()}?sections=${encodeURIComponent(ids.join(','))}`;
        const res = await fetch(url, {
            credentials: 'same-origin',
            headers: { 'Accept': 'application/json' }
        });
        if (!res.ok) throw new Error(`Section render failed (${res.status})`);
        const htmlMap = await res.json();
        ids.forEach(id => {
            const html = htmlMap[id];
            const mount = document.getElementById(`shopify-section-${id}`);
            if (html && mount) mount.outerHTML = html;
        });
        await updateCartCountFallback();
        document.dispatchEvent(new CustomEvent('rs:navigation:refresh'));
        window.dispatchEvent(new CustomEvent('cart:sections-refreshed', { detail: { ids } }));
    }
    
    function bindAddToCartButtons(context = document) {
        const buttons = context.querySelectorAll('.rs-add-to-cart-button');
        buttons.forEach((button) => {
            if (button.dataset.rsAtcBound === '1') return;
            button.dataset.rsAtcBound = '1';
            
            const root    = button.closest('add-to-cart-component') || button.parentElement;
            const form    = button.closest('form[data-type="add-to-cart-form"]') || button.closest('form');
            const wrapper = button.closest('.rs-buy-buttons-block') || root?.closest('.rs-buy-buttons-block') || document;
            const quantityWrapper = form?.querySelector('.rs-quantity-input-wrapper') ||
              button.closest('.rs-quantity-input-wrapper');
            
            const errorBox  = quantityWrapper?.querySelector('.rs-error__message') || wrapper.querySelector('.rs-error__message');
            const errorText = errorBox?.querySelector('.rs-error__message-text');
            
            const addedLabel = root?.querySelector('[ref="addedLabel"]');
            const labels     = root?.querySelectorAll('.rs-add-to-cart-button-label');
            
            if (errorBox) errorBox.style.display = '';
            
            function showError(msg) {
                showQuantityError(quantityWrapper, msg || /** @type {any} */ (window).__t('error_adding_to_cart'));
                button.disabled = false;
                button.removeAttribute('aria-busy');
                state.isAdding = false;
            }
            
            function hideError() {
                clearQuantityError(quantityWrapper);
            }
            
            function showAddedUI() {
                labels?.forEach(l => l.classList.add('rs-hidden'));
                if (addedLabel) addedLabel.classList.remove('rs-hidden');
                button.disabled = false;
                button.removeAttribute('aria-busy');
                hideError();
                button.dataset.added = '1'; // keep sticky "Added"
                state.isAdding = false;
            }
            
            if (button.dataset.added === '1') {
                labels?.forEach(l => l.classList.add('rs-hidden'));
                if (addedLabel) addedLabel.classList.remove('rs-hidden');
            }
            
            const state = { isAdding: false };
            
            async function addToCartOnce() {
                if (state.isAdding) return;
                state.isAdding = true;
                
                const variantInput = form?.querySelector('input[name="id"]');
                const skuInput =
                  form?.querySelector('input[name="sku"]') ||
                  form?.querySelector('input[name="material-number"]');
                const skuFromInput = skuInput instanceof HTMLInputElement ? skuInput.value?.trim() : '';
                const skuFromDom =
                  (form?.querySelector('[data-sku]')?.dataset?.sku ||
                    wrapper?.querySelector?.('[data-sku]')?.dataset?.sku ||
                    form?.closest('product-form-component')?.querySelector('[data-sku]')?.dataset?.sku ||
                    document.querySelector('[ref="priceContainer"][data-sku], product-price [data-sku]')?.dataset
                      ?.sku ||
                    '')?.trim?.() || '';
                const sku = skuFromInput || skuFromDom || undefined;
                const qtyInput = (form && form.querySelector('input[name="quantity"]')) ||
                  wrapper.querySelector('.rs-quantity-input');
                const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
                
                if (!variantInput || !variantInput.value) {
                    showError(/** @type {any} */ (window).__t('variant_not_selected'));
                    state.isAdding = false;
                    return;
                }

                if (qtyInput && !qtyInput.disabled) {
                    if (qtyInput.closest('.rs-buy-buttons-block') && typeof window.rsValidateProductCardQtyInput === 'function') {
                        if (window.rsValidateProductCardQtyInput(qtyInput) === false) {
                          state.isAdding = false;
                          return;
                        }
                    } else {
                        const min = resolveMinFromInput(qtyInput);
                        const step = resolveStepFromInput(qtyInput);
                        if (isNaN(quantity) || quantity < min) {
                            showError(/** @type {any} */ (window).__t('minimum_order', { count: min }));
                            state.isAdding = false;
                            return;
                        }
                        if (step > 1 && !isValidQuantity(quantity, min, step)) {
                            showError(/** @type {any} */ (window).__t('order_in_multiples', { count: step }));
                            state.isAdding = false;
                            return;
                        }
                    }
                    hideError();
                }

                button.disabled = true;
                button.setAttribute('aria-busy', 'true');
                hideError();
                
                const variantId = parseInt(variantInput.value, 10);
                const productForm = form?.closest('product-form-component');
                const lineProperties = window.mergeAddToCartLineProperties?.(form || button, {
                    ...(sku ? { _sku: sku } : {}),
                }, {
                    productId: productForm?.dataset?.productId,
                    variantId: variantInput.value,
                    sku: sku,
                }) || (sku ? { _sku: sku } : {});

                try {
                    // Check for an existing line item with this variant. If one exists,
                    // increment it via cart/change.js so Shopify doesn't create a duplicate
                    // line item (which happens when the existing item has properties that
                    // differ from the bare newly-added item).
                    const cartState = await fetch('/cart.js', { credentials: 'same-origin' }).then(r => r.json());
                    const existing = cartState.items?.find(/** @param {any} item */ (item) => item.variant_id === variantId);

                    if (existing) {
                        const changeRes = await fetch('/cart/change.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                            credentials: 'same-origin',
                            body: JSON.stringify({
                                id: existing.key,
                                quantity: existing.quantity + quantity,
                                properties: {
                                    ...(existing.properties || {}),
                                    ...Object.fromEntries(
                                        Object.entries(lineProperties).filter(([, value]) => value != null && value !== '')
                                    ),
                                },
                            }),
                        });
                        const changeJson = await changeRes.json().catch(() => ({}));
                        if (!changeRes.ok) throw new Error(changeJson?.description || changeJson?.message || `Failed to update cart (HTTP ${changeRes.status})`);
                    } else {
                        const maxLines = typeof window.getMaxCartLineItems === 'function'
                            ? window.getMaxCartLineItems()
                            : getMaxCartLineItems();
                        if ((cartState.items?.length || 0) >= maxLines) {
                            throw new Error(
                              (typeof window.cartLineLimitMessage === 'function'
                                ? window.cartLineLimitMessage(maxLines)
                                : cartLineLimitMessage(maxLines)),
                            );
                        }

                        // BPM-5360: include realtime price + plant on the initial add, not a follow-up cart change.
                        const lineItem = {
                            id: variantId,
                            quantity,
                            ...(Object.keys(lineProperties).length ? { properties: lineProperties } : {}),
                        };

                        const res = await fetch('/cart/add.js', {
                            method: 'POST',
                            headers: {
                                'Accept': 'application/json',
                                'Content-Type': 'application/json',
                                'X-Requested-With': 'XMLHttpRequest',
                            },
                            credentials: 'same-origin',
                            body: JSON.stringify({ items: [lineItem] }),
                        });

                        const json = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(json?.description || json?.message || `Failed to add to cart (HTTP ${res.status})`);
                    }
                    
                    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'atc' } }));
                    
                    showAddedUI();
                } catch (err) {
                    console.error('error', err);
                    showError(err.message || 'Error adding to cart. Please try again.');
                    state.isAdding = false;
                    button.disabled = false;
                    button.removeAttribute('aria-busy');
                }
            }
            
            if (form && form.dataset.rsAtcSubmitBound !== '1') {
                form.addEventListener('submit', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
                    addToCartOnce();
                }, { capture: true });
                form.dataset.rsAtcSubmitBound = '1';
            }
            
            button.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
                addToCartOnce();
            }, { capture: true });
        });
    }
    
    bindAddToCartButtons();
    
    document.addEventListener('shopify:section:load', (e) => bindAddToCartButtons(e.target));
    window.addEventListener('cart:sections-refreshed', () => bindAddToCartButtons(document));

    // Listens to 'cart:updated' event to refresh navigation cart item count after PLP add-to-cart //
    document.addEventListener('cart:updated', async (event) => {
        try {
            const skipSectionRefresh = event?.detail?.skipSectionRefresh === true;
            const quickOrderModalOpen = !!document.querySelector(
                'quick-add-to-cart-modal.rs-quick-add-to-cart-modal:not(.rs-hidden)'
            );
            // Full section refresh replaces DOM nodes and would destroy an open Quick Order ATC dialog.
            if (skipSectionRefresh || quickOrderModalOpen) {
                await updateCartCountFallback();
                return;
            }
            await refreshSections();
        } catch (e) {
            console.error('Section refresh failed, using count fallback:', e);
            await updateCartCountFallback();
        }
    });

    const registerButton = document.querySelectorAll('.rs-register');
    registerButton.forEach(button => {
      const resideoIdRegisterLink = document.querySelector('input[name="resideo_id_register_link"]');
      if (!resideoIdRegisterLink) {
        return;
      }
      let url = new URL(resideoIdRegisterLink.getAttribute('value'));
      url += `?return_to=${window.location.pathname}`;
      button.href = url.toString();
    });

});
